import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import MD5 from 'crypto-js/md5'
import { v4 as makeUuid } from 'uuid'

import { SenderPolicy, SenderPolicyStatus } from 'types/Settings'
import {
  SenderPoliciesForm,
  useSenderPoliciesForm
} from 'components/pages/inboundSettings/senderPolicies/useSenderPoliciesForm'
import { SenderPoliciesPagination } from 'components/pages/inboundSettings/senderPolicies/useSenderPoliciesPagination'

export interface UseSenderPoliciesTableLogicProps {
  pagination: SenderPoliciesPagination
  availablePolicies: SenderPolicyStatus[]
  onAddItem: (item: SenderPolicy) => void
  onRemoveItem: (id: string) => void
  delegateDirtyFormState: Dispatch<SetStateAction<boolean>>
}

export interface State {
  idToRemove: string
  form: SenderPoliciesForm
}

export interface EventHandlers {
  onAdd: () => void
  onRemove: (id: string) => void
  onConfirmRemove: () => void
  onConfirmCancel: () => void
  handleOnInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  onPolicyChange: (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => void
}

export type UseSenderPoliciesTableLogic = [State, EventHandlers]

export const enum POLICIES_INPUT_NAMES {
  NAME = 'name',
  POLICY = 'policy',
  COMMENT = 'comment'
}

const INITIAL_FORM_STATE = { name: '', comment: '', policy: SenderPolicyStatus.block }

export const useSenderPoliciesTableLogic = ({
  pagination,
  onAddItem,
  onRemoveItem,
  delegateDirtyFormState
}: UseSenderPoliciesTableLogicProps): UseSenderPoliciesTableLogic => {
  const form = useSenderPoliciesForm({
    initialState: INITIAL_FORM_STATE,
    delegateIsDirtyForm: delegateDirtyFormState
  })
  const [idToRemove, setIdToRemove] = useState('')

  const onAdd = useCallback(() => {
    const newPolicy: SenderPolicy = {
      ...form.getState(),
      modified: Date.now() / 1000,
      id: MD5(makeUuid()).toString()
    }
    if (form.validate(newPolicy, { data: pagination.data })) {
      onAddItem(newPolicy)
    }
  }, [form, pagination.data, onAddItem])

  const onRemove = useCallback(
    (id: string) => {
      setIdToRemove(id)
    },
    [setIdToRemove]
  )

  const onConfirmRemove = useCallback(() => {
    onRemoveItem(idToRemove)
    setIdToRemove('')
  }, [idToRemove, onRemoveItem, setIdToRemove])

  const onConfirmCancel = useCallback(() => {
    setIdToRemove('')
  }, [setIdToRemove])

  const handleOnInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      // eslint-disable-next-line default-case
      switch (true) {
        case name === POLICIES_INPUT_NAMES.NAME:
          form.fields.name.setValue(value)
          break
        case name === POLICIES_INPUT_NAMES.COMMENT:
          form.fields.comment.setValue(value)
          break
      }
      form.delegateIsDirty()
    },
    [form]
  )

  const onPolicyChange = useCallback(
    (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      form.fields.policy.setValue(e.target.value as SenderPolicyStatus)
      form.delegateIsDirty()
    },
    [form]
  )

  return useMemo(
    () => [
      {
        idToRemove,
        form
      },
      {
        onAdd,
        onRemove,
        onConfirmRemove,
        onConfirmCancel,
        handleOnInputChange,
        onPolicyChange
      }
    ],
    [idToRemove, form, onAdd, onRemove, onConfirmRemove, onConfirmCancel, handleOnInputChange, onPolicyChange]
  )
}
